import { registry } from '../components/registry'
import { startAnchorDetails } from '../lib/anchorDetails'
import { startCharacterCounters } from '../ui/characterCounter'
import { startConfirmForms } from '../ui/confirmForm'
import { startCopyButtons } from '../ui/copyButton'
import { startDeviceSettings } from '../ui/deviceSettings'
import { startAccountMenu } from '../ui/navMenu'
import { startPinterestPinForm } from '../ui/pinterestPinForm'
import { startTemplateTranslate } from '../ui/templateTranslate'
import { startGuestImport } from '../lib/startGuestImport'
import { startGuestUpload } from '../landing/guestUpload'
import { startPushSettings } from '../lib/push'
import { startServiceWorker } from '../lib/serviceWorker'
import { startThemeToggle } from '../ui/theme'
import './application.css'

/**
 * Browser error monitoring, off unless the server says otherwise.
 *
 * The layout renders `<meta name="sentry-dsn">` only when SENTRY_DSN is set
 * (config/sentry_settings.rb), so a deployment that has not signed up for a
 * Sentry project gets no tag, no import and no SDK. The import is dynamic for
 * exactly that reason: a static one would put @sentry/browser into a bundle
 * that is already over Vite's size warning, for every visitor, to do nothing.
 *
 * Failures here are swallowed. Monitoring that takes the page down with it
 * when the CDN chunk 404s would be worse than no monitoring.
 */
function metaContent(name: string): string | null {
  const value = document.querySelector(`meta[name="${name}"]`)?.getAttribute('content')
  return value ? value : null
}

function startErrorMonitoring(): void {
  const dsn = metaContent('sentry-dsn')
  if (!dsn) return

  void import('@sentry/browser')
    .then((Sentry) => {
      Sentry.init({
        dsn,
        environment: metaContent('sentry-environment') ?? 'production',
        // The deployed KAMAL_VERSION, same tag as the server events; absent
        // outside a Kamal container, and Sentry treats undefined as unset.
        release: metaContent('sentry-release') ?? undefined,
        // The editor's documents are the user's work; nothing of them belongs
        // in a crash report. Same stance as the Ruby side.
        sendDefaultPii: false,
      })
    })
    .catch((error: unknown) => {
      console.warn('Error monitoring did not start', error)
    })
}

/**
 * Die React-Montagestellen der Seite -- asynchron, weil jede ihr eigenes
 * Bündel hat.
 *
 * Zwei Ansichten von rund vierzig montieren React (projects/index und
 * projects/show). Alles, was diese Datei statisch importiert, laden aber auch
 * die Startseite, die Preisseite, das Impressum, der Katalog und die
 * Kontoseite -- deshalb hängt hier weder React noch eine Komponente, sondern
 * nur die Tabelle der Lader (F-12). Gibt es keine Montagestelle, wird kein
 * einziges weiteres Bündel geholt.
 *
 * Der Fang bleibt pro Stelle (F-6), jetzt an zwei Stellen: das `data-props`
 * wird weiterhin *vor* dem Laden gelesen, damit ein unlesbares Attribut nicht
 * erst ein Bündel herunterlädt, um dann zu scheitern; und das Nachladen selbst
 * hat sein eigenes `catch` -- ein 404 auf ein Bündel ist der Fehler dieser
 * einen Stelle und nimmt weder die zweite noch den Rest der Seite mit.
 */
function mountReactComponents(): void {
  const mountPoints = document.querySelectorAll<HTMLElement>('[data-react-component]')

  mountPoints.forEach((element) => {
    const name = element.dataset.reactComponent
    if (!name) return

    const load = registry[name]
    if (!load) {
      console.error(`Unknown React component "${name}" for data-react-component`)
      return
    }

    let props: unknown
    try {
      props = element.dataset.props ? JSON.parse(element.dataset.props) : {}
    } catch (error) {
      console.error(`React component "${name}" did not mount`, error)
      return
    }

    // Beide Bündel parallel: der Montagehelfer (React selbst) und die
    // Komponente hängen nicht voneinander ab, hintereinander wären es zwei
    // Rundgänge statt einem.
    void Promise.all([import('../components/mount'), load()])
      .then(([{ mountComponent }, module]) => {
        mountComponent(element, module.default, props)
      })
      .catch((error: unknown) => {
        console.error(`React component "${name}" did not mount`, error)
      })
  })
}

// Eine Anreicherung, die scheitern darf, ohne die nächste mitzunehmen.
function start(label: string, initialise: () => void): void {
  try {
    initialise()
  } catch (error) {
    console.error(`${label} did not start`, error)
  }
}

startErrorMonitoring()

/**
 * Die Reihenfolge ist Absicht.
 *
 * Der Themenschalter, die Zeichenzähler, die Kopierknöpfe und der Gerätefilter
 * gehören zu den server-gerenderten Seiten und stehen auf fast jeder; React
 * wird nur auf zweien montiert. Das Kleine, das überall zählt, läuft deshalb
 * zuerst -- und jeder Teil in seinem eigenen Fang, damit keiner den nächsten
 * mitnimmt.
 */
document.addEventListener('DOMContentLoaded', () => {
  // Zuerst: das hier ist die Rückfrage vor „Konto löschen" und „Vorlage
  // entfernen". Sie darf nicht ausfallen, weil weiter unten etwas anderes
  // stolpert (app/frontend/ui/confirmForm.ts).
  start('The confirmation dialogs', startConfirmForms)
  start('The theme toggle', startThemeToggle)
  // Das Kontomenü öffnet auch ohne dieses Skript (popovertarget); hier
  // bekommt es nur Position und aria-expanded (app/frontend/ui/navMenu.ts).
  start('The account menu', startAccountMenu)
  start('The character counters', startCharacterCounters)
  start('The copy buttons', startCopyButtons)
  start('The Pinterest pin form', startPinterestPinForm)
  start('The template translate button', startTemplateTranslate)
  start('The device settings', startDeviceSettings)
  // Die Upload-Fläche im Hero der Startseite -- Vanilla-TS statt eines
  // React-Chunks, weil die Startseite genau ein Bündel lädt
  // (app/frontend/landing/guestUpload.ts). Auf jeder anderen Seite findet
  // sie nichts und kehrt sofort zurück.
  start('The landing upload', startGuestUpload)
  start('The React components', mountReactComponents)
  // Öffnet das umgebende `<details>` für einen `#kommentar-…`-Anker, der
  // sonst hinter einer eingeklappten Antwortliste verschwände
  // (app/frontend/lib/anchorDetails.ts).
  start('The comment anchor', startAnchorDetails)
  // Zuletzt und ohne Eile: der Worker ist für den nächsten Besuch da, nicht
  // für diesen (app/frontend/lib/serviceWorker.ts).
  start('The service worker', startServiceWorker)
  // Was jemand ohne Konto getraced hat, ins frisch angelegte Konto holen
  // (app/frontend/lib/startGuestImport.ts). Nach dem Worker, weil dessen
  // Kontowechsel-Guard die IndexedDB leeren *darf* -- ein Ergebnis, das einem
  // anderen Konto gehört, soll gar nicht erst übernommen werden.
  start('The guest import', startGuestImport)
  // Die Push-Karte auf /konto -- findet auf jeder anderen Seite nichts und
  // kehrt sofort zurück (app/frontend/lib/push.ts). Nach dem Worker, weil
  // sie auf dessen Registrierung wartet (`navigator.serviceWorker.ready`).
  start('Push notifications', startPushSettings)
})
